/**
 * Shared page shell components for workspace pages.
 *
 * PageShell  — Page layout that portals title + tabs into the workspace
 *              top-bar (`ViewHeader`) and renders the optional filter row
 *              + scrollable body inside `ViewBody`.
 * FilterBar  — Search input + pill-style filter buttons (also usable standalone).
 *
 * Layout invariant: title and primary nav-tabs MUST live in the top-bar via
 * `<ViewHeader>` so workflows / marketplace / custom-apps match the doc
 * view (which also injects its title there). Rendering them inside the body
 * leaves the top-bar empty and creates a visually stacked second header.
 */
import { PageDetailLoading } from '@affine/component/page-detail-skeleton';
import { Header } from '@affine/core/components/pure/header';
import {
  ViewBody,
  ViewHeader,
  ViewTitle,
} from '@affine/core/modules/workbench';
import clsx from 'clsx';
import type { HTMLAttributes, ReactElement, ReactNode, UIEvent } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as styles from './page-shell.css';

// =============================================================================
// Types
// =============================================================================

export interface Tab {
  key: string;
  label: string;
}

export interface Filter {
  key: string;
  label: string;
}

interface PageChromeCollapseContextValue {
  heroCollapseProgress: number;
  filterCollapseProgress: number;
  onBodyScroll: (event: UIEvent<HTMLDivElement>) => void;
}

const PageChromeCollapseContext =
  createContext<PageChromeCollapseContextValue | null>(null);

const pageChromeCollapseQuery = 'screen and (max-width: 720px)';

function canCollapsePageChrome(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(pageChromeCollapseQuery).matches
  );
}

export function usePageChromeCollapse(): PageChromeCollapseContextValue | null {
  return useContext(PageChromeCollapseContext);
}

// =============================================================================
// PageShell — unified page layout
// =============================================================================

interface PageShellProps {
  title: string;
  icon?: ReactNode;
  /** Page-level nav tabs (underline style, shown in top-bar next to title). */
  tabs?: Tab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  /**
   * Optional filter row (search + pill filters). Rendered as a sub-header
   * directly under the workspace top-bar with its own bottom border.
   */
  filter?: ReactNode;
  /** Page content. */
  children: ReactNode;
}

export function PageShell({
  title,
  icon,
  tabs,
  activeTab,
  onTabChange,
  filter,
  children,
}: PageShellProps): ReactElement {
  return (
    <>
      <ViewTitle title={title} />
      <ViewHeader>
        <Header
          left={
            <div className={styles.viewHeaderInner}>
              {icon ? <span className={styles.headerIcon}>{icon}</span> : null}
              <h1 className={styles.headerTitle}>{title}</h1>
              {tabs?.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  className={clsx(
                    styles.headerTab,
                    activeTab === tab.key && styles.headerTabActive
                  )}
                  onClick={() => onTabChange?.(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          }
        />
      </ViewHeader>
      <ViewBody>
        <div className={styles.pageContainer}>
          {filter && <div className={styles.filterRegion}>{filter}</div>}
          <div className={styles.pageBody}>{children}</div>
        </div>
      </ViewBody>
    </>
  );
}

export function PageShellLoading({
  icon,
  title,
}: {
  icon?: ReactNode;
  title: string;
}): ReactElement {
  return (
    <PageShell icon={icon} title={title}>
      <PageDetailLoading />
    </PageShell>
  );
}

// =============================================================================
// PageContent — standard wrapper for tab content with Hero + Body.
// Provides flex-col + overflow-hidden so children own their scroll.
// =============================================================================

export function PageContent({
  children,
  className,
  ...props
}: {
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>): ReactElement {
  const lastScrollTopRef = useRef(0);
  const progressRef = useRef({ hero: 0, filter: 0 });
  const [chromeProgress, setChromeProgress] = useState({
    hero: 0,
    filter: 0,
  });

  const resetChromeProgress = useCallback((scrollTop = 0) => {
    lastScrollTopRef.current = scrollTop;

    const previous = progressRef.current;
    if (previous.hero === 0 && previous.filter === 0) {
      return;
    }

    const nextProgress = { hero: 0, filter: 0 };
    progressRef.current = nextProgress;
    setChromeProgress(nextProgress);
  }, []);

  const onBodyScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const scrollTop = event.currentTarget.scrollTop;

      if (!canCollapsePageChrome()) {
        resetChromeProgress(scrollTop);
        return;
      }

      const previousScrollTop = lastScrollTopRef.current;
      const delta = scrollTop - previousScrollTop;

      lastScrollTopRef.current = scrollTop;

      const clamp = (value: number) => Math.min(1, Math.max(0, value));
      const hero = scrollTop <= 2 ? 0 : clamp((scrollTop - 4) / 104);
      const previous = progressRef.current;
      let filter = previous.filter;

      if (scrollTop <= 2) {
        filter = 0;
      } else if (delta > 1 && scrollTop > 8) {
        filter = clamp(filter + delta / 64);
      } else if (delta < -1) {
        filter = clamp(filter + delta / 48);
      }

      if (
        Math.abs(previous.hero - hero) < 0.01 &&
        Math.abs(previous.filter - filter) < 0.01
      ) {
        return;
      }

      const nextProgress = { hero, filter };
      progressRef.current = nextProgress;
      setChromeProgress(nextProgress);
    },
    [resetChromeProgress]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(pageChromeCollapseQuery);
    const onMediaChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        resetChromeProgress(0);
      }
    };

    if (!mediaQuery.matches) {
      resetChromeProgress(0);
    }

    mediaQuery.addEventListener('change', onMediaChange);
    return () => {
      mediaQuery.removeEventListener('change', onMediaChange);
    };
  }, [resetChromeProgress]);

  return (
    <PageChromeCollapseContext.Provider
      value={{
        heroCollapseProgress: chromeProgress.hero,
        filterCollapseProgress: chromeProgress.filter,
        onBodyScroll,
      }}
    >
      <div className={clsx(styles.pageContent, className)} {...props}>
        {children}
      </div>
    </PageChromeCollapseContext.Provider>
  );
}

// =============================================================================
// FilterBar — search + pill filters (used inside PageShell body)
// =============================================================================

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters: Filter[];
  activeFilter: string;
  onFilterChange: (key: string) => void;
  /** Optional trailing content (e.g. sort dropdown). */
  children?: ReactNode;
}

export function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters,
  activeFilter,
  onFilterChange,
  children,
}: FilterBarProps): ReactElement {
  return (
    <div className={styles.filterBar}>
      <input
        type="text"
        className={styles.filterSearch}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={e => onSearchChange(e.target.value)}
      />
      {filters.map(f => (
        <button
          key={f.key}
          type="button"
          className={clsx(
            styles.filterPill,
            activeFilter === f.key && styles.filterPillActive
          )}
          onClick={() => onFilterChange(f.key)}
        >
          {f.label}
        </button>
      ))}
      {children}
    </div>
  );
}
